import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Lead APIs
export const createLead = (data) => api.post('/leads', data);
export const submitContactForm = (data) => api.post('/contact', data);
export const requestCallback = (data) => api.post('/callback-request', data);
export const registerWebinar = (data) => api.post('/webinar/register', data);

// AWS Track waitlist
export const joinAwsWaitlist = (data) => api.post('/aws-waitlist/signup', data);

// Team enrollment
export const submitTeamEnrollment = (data) => api.post('/team-enrollment/submit', data);
export const getTeamEnrollmentPricing = () => api.get('/team-enrollment/pricing');

// Admin — unified enrollment inquiries
export const getAdminEnrollments = (params = {}) => api.get('/admin/enrollments', { params });
export const getAdminEnrollmentStats = () => api.get('/admin/enrollments/stats');
export const updateAdminEnrollment = (source, docId, updates) => api.patch(`/admin/enrollments/${source}/${docId}`, updates);

// Pricing APIs
export const getPricing = () => api.get('/pricing');
export const getERPPricing = (erp) => api.get(`/pricing/${erp}`);

// Payment APIs
export const createCheckoutSession = (data) => api.post('/payments/checkout', data);
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);

// Admin Auth APIs
export const adminLogin = (data) => api.post('/admin/login', data);
export const adminRegister = (data) => api.post('/admin/register', data);
export const getAdminProfile = () => api.get('/admin/me');

// Admin Data APIs
export const getAdminLeads = (params) => api.get('/admin/leads', { params });
export const getAdminLead = (id) => api.get(`/admin/leads/${id}`);
export const updateAdminLead = (id, data) => api.put(`/admin/leads/${id}`, data);
export const deleteAdminLead = (id) => api.delete(`/admin/leads/${id}`);
export const getWebinarRegistrations = () => api.get('/admin/webinar-registrations');
export const getAdminPayments = () => api.get('/admin/payments');
export const getDashboardStats = () => api.get('/admin/stats');

// ERP Program Data
export const erpPrograms = {
  ifs: {
    name: 'IFS',
    fullName: 'IFS Cloud ERP',
    tagline: 'Master Enterprise Asset Management & Service Excellence',
    description: 'IFS ERP is a cloud-based Enterprise Resource Planning solution that integrates data and processes across multiple departments. Our comprehensive training covers IFS Application architecture, Report Designer, Solution Manager, and real-time scenario-based implementations.',
    icon: '🏭',
    color: 'from-blue-500 to-blue-700',
    industries: ['Aerospace & Defense', 'Manufacturing', 'Oil & Gas', 'Energy & Utilities', 'Construction', 'Engineering', 'Service Management'],
    modules: [
      'IFS Application Architecture & Navigation',
      'IFS Report Designer Tool',
      'IFS Solution Manager',
      'Enterprise Asset Management (EAM)',
      'Field Service Management',
      'Project-Oriented Production',
      'Supply Chain & Inventory Management',
      'Financial Management & Accounting',
      'Document Management',
      'Human Capital Management',
      'Business Process Control',
      'IFS Cloud Configuration'
    ],
    certifications: [
      'IFS Applications Functional Consultant',
      'IFS Cloud Implementation Specialist',
      'IFS EAM Certified Professional',
      'IFS Service Management Specialist',
      'IFS Technical Developer Certification'
    ],
    jobRoles: [
      'IFS Functional Consultant',
      'IFS Technical Consultant',
      'IFS Solution Architect',
      'EAM Specialist',
      'Field Service Management Consultant',
      'IFS Project Manager',
      'IFS Support Analyst',
      'IFS Integration Specialist'
    ],
    // Enhanced IFS-specific data
    trainingPaths: [
      {
        name: 'IFS Functional Track',
        duration: '8-10 weeks',
        level: 'Beginner to Intermediate',
        topics: [
          'IFS Cloud Overview & Navigation',
          'Core Module Configuration',
          'Business Process Mapping',
          'Master Data Management',
          'Reporting & Analytics'
        ]
      },
      {
        name: 'IFS Technical Track',
        duration: '10-12 weeks',
        level: 'Intermediate to Advanced',
        topics: [
          'IFS Application Development',
          'IFS Report Designer Advanced',
          'Integration Architecture',
          'Custom Extensions & APIs',
          'Performance Optimization'
        ]
      },
      {
        name: 'IFS EAM Specialist',
        duration: '6-8 weeks',
        level: 'Specialized',
        topics: [
          'Asset Lifecycle Management',
          'Preventive & Predictive Maintenance',
          'Work Order Management',
          'Mobile Workforce Solutions',
          'IoT Integration'
        ]
      }
    ],
    keyFeatures: [
      'Real-time scenario-based case studies',
      'Access to IFS sandbox environment',
      'Industry-certified expert instructors',
      'Project-oriented production methodologies',
      'Customizable content for corporate teams',
      'Certificate recognized by industry leaders'
    ],
    corporateClients: ['DHL', 'Lamprell', 'ESSAR', 'Ashok Leyland', 'SABIC', 'TSPL'],
    examPrep: true,
    handsOnLabs: true,
    projectWork: true
  },
  odoo: {
    name: 'Odoo',
    fullName: 'Odoo ERP',
    tagline: 'Open-Source ERP for Modern Business',
    description: 'Odoo is the most installed business software in the world. Master this versatile, modular ERP system covering CRM, sales, inventory, manufacturing, accounting, and more.',
    icon: '🔧',
    color: 'from-purple-500 to-purple-700',
    industries: ['Retail', 'Manufacturing', 'Services', 'E-commerce', 'Healthcare'],
    modules: [
      'Sales & CRM',
      'Inventory Management',
      'Manufacturing (MRP)',
      'Accounting & Finance',
      'Human Resources',
      'E-commerce & Website',
      'Project Management'
    ],
    certifications: ['Odoo Functional Certification', 'Odoo Technical Certification'],
    jobRoles: ['Odoo Functional Consultant', 'Odoo Developer', 'Odoo Implementation Specialist', 'Business Analyst']
  },
  oracle: {
    name: 'Oracle',
    fullName: 'Oracle Cloud ERP',
    tagline: 'Enterprise-Grade Cloud Solutions',
    description: 'Oracle Cloud ERP is a complete, innovative, and proven solution for organizations of all sizes. Learn to implement and manage Oracle Fusion Cloud Applications.',
    icon: '☁️',
    color: 'from-red-500 to-red-700',
    industries: ['Financial Services', 'Healthcare', 'Public Sector', 'Retail', 'Technology'],
    modules: [
      'Oracle Financials Cloud',
      'Oracle Procurement Cloud',
      'Oracle Project Portfolio Management',
      'Oracle Risk Management',
      'Oracle Enterprise Performance Management',
      'Oracle Supply Chain Management',
      'Oracle HCM Cloud'
    ],
    certifications: ['Oracle Cloud Infrastructure Certified', 'Oracle Financials Cloud Certified Implementation Specialist'],
    jobRoles: ['Oracle Cloud Consultant', 'Oracle Financials Analyst', 'Oracle Technical Architect', 'Oracle DBA']
  },
  netsuite: {
    name: 'NetSuite',
    fullName: 'Oracle NetSuite',
    tagline: 'Cloud ERP for Growing Businesses',
    description: 'NetSuite is the world\'s leading integrated cloud business software suite. Perfect for fast-growing companies needing scalable financial, CRM, and e-commerce solutions.',
    icon: '📊',
    color: 'from-teal-500 to-teal-700',
    industries: ['Software & Technology', 'Wholesale Distribution', 'Retail', 'Services', 'Non-Profit'],
    modules: [
      'Financial Management',
      'Order Management',
      'Inventory & Supply Chain',
      'CRM & Sales Force Automation',
      'E-commerce (SuiteCommerce)',
      'Professional Services Automation',
      'SuiteAnalytics'
    ],
    certifications: ['NetSuite ERP Consultant', 'NetSuite Administrator', 'SuiteFoundation'],
    jobRoles: ['NetSuite Consultant', 'NetSuite Administrator', 'NetSuite Developer', 'Business Systems Analyst']
  },
  infor: {
    name: 'Infor',
    fullName: 'Infor CloudSuite',
    tagline: 'Industry-Specific Cloud ERP',
    description: 'Infor delivers industry-specific cloud applications built on decades of deep industry expertise. Specialize in solutions for manufacturing, healthcare, hospitality, and more.',
    icon: '🏢',
    color: 'from-green-500 to-green-700',
    industries: ['Healthcare', 'Manufacturing', 'Hospitality', 'Fashion', 'Food & Beverage'],
    modules: [
      'Infor CloudSuite Industrial',
      'Infor M3 (Manufacturing)',
      'Infor LN',
      'Infor Birst Analytics',
      'Infor Nexus (Supply Chain)',
      'Infor HCM',
      'Infor OS (Operating Service)'
    ],
    certifications: ['Infor Certified Consultant', 'Infor M3 Specialist'],
    jobRoles: ['Infor Functional Consultant', 'Infor Technical Consultant', 'M3 Specialist', 'CloudSuite Architect']
  }
};

export const trainingTracks = {
  career_starter: {
    name: 'Career Starter',
    subtitle: 'Beginners & Job Seekers',
    description: 'Perfect for fresh graduates and career changers looking to enter the ERP industry.',
    features: [
      'ERP fundamentals and core concepts',
      'Business process understanding',
      'Entry-level job preparation',
      'Resume building & interview prep',
      'Hands-on practice labs'
    ],
    duration: '8-10 weeks',
    icon: '🚀'
  },
  professional: {
    name: 'Professional Upskilling',
    subtitle: 'IT Professionals',
    description: 'Advance your career with specialized skills in ERP configuration, customization, and implementation.',
    features: [
      'Advanced configuration techniques',
      'Technical/functional specialization',
      'Certification exam preparation',
      'Real-world project experience',
      'Best practices & methodologies'
    ],
    duration: '10-12 weeks',
    icon: '💼'
  },
  business_user: {
    name: 'Business User Enablement',
    subtitle: 'Business Professionals',
    description: 'Empower your business users to maximize ERP system efficiency and drive organizational value.',
    features: [
      'Day-to-day operational usage',
      'Reporting & analytics dashboards',
      'Process optimization techniques',
      'Data-driven decision making',
      'Cross-functional collaboration'
    ],
    duration: '6-8 weeks',
    icon: '📈'
  }
};
